/* ==========================================================================
   screenbuddy — global.js
   Small, dependency-free helpers shared by every page.
   Page-specific modules (cart drawer, scroll sequence, gallery …) live in
   their own files and are loaded by the sections that need them.
   ========================================================================== */

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

function trapFocus(container) {
  const nodes = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
  if (!nodes.length) return () => {};
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  const onKey = (event) => {
    if (event.key !== 'Tab') return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
  container.addEventListener('keydown', onKey);
  first.focus();
  return () => container.removeEventListener('keydown', onKey);
}
window.trapFocus = trapFocus;

/* ---------- <menu-drawer>: mobile navigation ---------- */
class MenuDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-menu-toggle]');
    this.panel = this.querySelector('[data-menu-panel]');
    this.overlay = this.querySelector('[data-menu-overlay]');
    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));
    this.overlay?.addEventListener('click', () => this.close());
    this.querySelectorAll('[data-menu-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close();
    });
  }

  get isOpen() {
    return this.toggle.getAttribute('aria-expanded') === 'true';
  }

  open() {
    this.toggle.setAttribute('aria-expanded', 'true');
    this.panel.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.releaseFocus = trapFocus(this.panel);
  }

  close() {
    this.toggle.setAttribute('aria-expanded', 'false');
    this.panel.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
    this.releaseFocus?.();
    this.toggle.focus();
  }
}
customElements.define('menu-drawer', MenuDrawer);

/* ---------- Desktop dropdowns: close other <details> when one opens ---------- */
document.addEventListener('toggle', (event) => {
  const el = event.target;
  if (!(el instanceof HTMLDetailsElement) || !el.matches('[data-dropdown]') || !el.open) return;
  document.querySelectorAll('details[data-dropdown][open]').forEach((other) => {
    if (other !== el) other.open = false;
  });
}, true);

document.addEventListener('click', (event) => {
  document.querySelectorAll('details[data-dropdown][open]').forEach((el) => {
    if (!el.contains(event.target)) el.open = false;
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('details[data-dropdown][open]').forEach((el) => {
    el.open = false;
    el.querySelector('summary')?.focus();
  });
});

/* ---------- Cart count: other modules dispatch 'cart:updated' ---------- */
document.addEventListener('cart:updated', (event) => {
  const count = event.detail?.item_count;
  if (typeof count !== 'number') return;
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.hidden = count === 0;
  });
});

/* ---------- <deferred-media>: load video/iframe only on click ---------- */
if (!customElements.get('deferred-media')) {
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-deferred-play]')?.addEventListener('click', () => this.load());
    }

    load() {
      const template = this.querySelector('[data-deferred-template]');
      if (!template || this.dataset.loaded) return;
      this.dataset.loaded = 'true';
      const content = template.content.cloneNode(true);
      this.querySelector('[data-deferred-overlay]')?.remove();
      this.querySelector('.video-section__poster')?.remove();
      this.appendChild(content);
      const video = this.querySelector('video');
      if (video) {
        video.play?.().catch(() => {});
        video.focus();
      }
    }
  }
  customElements.define('deferred-media', DeferredMedia);
}

/* ---------- Localization form: submit on change ---------- */
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-localization-select]');
  if (select) select.form.submit();
});

/*
  Brand name: every visible "screenbuddy" becomes screen<b>buddy</b><sup>®</sup>.
  Works on all text (theme, admin content, product titles, apps) and on content
  added later (cart drawer, section rendering). Skips URLs, handles and e-mail
  addresses (screenbuddy.us, screenbuddy_connectedview, screenbuddy-dashboard).
*/
(() => {
  const PATTERN = /(^|[^\w@#/.\-])screenbuddy(?:[ \u00a0]?(?:®|Ⓡ|\(r\)))?(?![\w\-]|\.[a-z0-9])/gi;
  const SKIP = 'script, style, noscript, textarea, input, select, option, code, pre, svg, title, [contenteditable], .brand-name, [data-no-brand]';

  const wrapNode = (node) => {
    const text = node.nodeValue;
    PATTERN.lastIndex = 0;
    if (!PATTERN.test(text)) return;
    const parent = node.parentElement;
    if (!parent || parent.closest(SKIP)) return;
    PATTERN.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let last = 0;
    let match;
    while ((match = PATTERN.exec(text))) {
      const start = match.index + match[1].length;
      fragment.append(text.slice(last, start));
      const brand = document.createElement('span');
      brand.className = 'brand-name';
      brand.innerHTML = 'screen<b>buddy</b><sup>®</sup>';
      fragment.append(brand);
      last = PATTERN.lastIndex;
    }
    fragment.append(text.slice(last));
    node.replaceWith(fragment);
  };

  const scan = (root) => {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) return wrapNode(root);
    if (root.nodeType !== Node.ELEMENT_NODE || root.closest(SKIP)) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (/screenbuddy/i.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(wrapNode);
  };

  const start = () => {
    scan(document.body);
    new MutationObserver((mutations) => {
      mutations.forEach((m) => m.addedNodes.forEach(scan));
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* Header "Jetzt kaufen": jump to the buy box when the page has one (home, landing pages). */
(() => {
  const jump = () => {
    const buyBox = document.getElementById('kaufen');
    if (!buyBox) return;
    document.querySelectorAll('.site-header__cta, [data-buy-jump]').forEach((link) => {
      link.setAttribute('href', '#kaufen');
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', jump);
  else jump();
})();
